import React, { useEffect, useState } from 'react';
import { X, Clock, AlertTriangle, ChevronRight, User, Calendar, ExternalLink, History, Activity } from 'lucide-react';
import api from './axiosConfig';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const IncidentHistoryModal = ({ isOpen, onClose, assetnum, locationId, title }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      const fetchHistory = async () => {
        setLoading(true);
        try {
            console.log("Fetching history with params:", { assetnum, locationId });
          const params = assetnum ? { assetnum: assetnum } : { location_id: locationId };
          const res = await api.get('/incidents/history', { params });
          setHistory(res.data);
        } catch (err) {
          console.error("Failed to load history");
        } finally {
          setLoading(false);
        }
      };
      fetchHistory();
    }
  }, [isOpen, assetnum, locationId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-panel w-[700px] h-[85vh] rounded-lg shadow-2xl overflow-hidden animate-in zoom-in duration-200 flex flex-col border border-line">

        {/* Header - نفس روح نظام تكنويا */}
        <div className="bg-primary-dark text-ink-inverse p-4 flex justify-between items-center shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-accent p-1.5 rounded-sm">
              <History size={18} className="text-ink-inverse" />
            </div>
            <div>
              <h2 className="font-black uppercase tracking-tighter text-sm">{t('incidentHistoryModal.title')}</h2>
              <p className="text-[9px] text-ink-inverse/70 font-bold uppercase tracking-widest">{title}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-ink-inverse/10 p-1 rounded-full transition-all"><X size={22}/></button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center space-y-3 opacity-50">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
              <p className="font-black text-[10px] uppercase tracking-[0.3em] text-primary">{t('incidentHistoryModal.retrieving')}</p>
            </div>
          ) : history.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center opacity-20">
              <Clock size={64} className="mb-2" />
              <p className="font-black uppercase tracking-[0.2em] text-lg">{t('incidentHistoryModal.noRecords')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-surface-card p-2 px-4 rounded border-b border-line flex justify-between items-center shadow-sm">
                 <span className="text-[10px] font-black text-ink-secondary uppercase">{t('incidentHistoryModal.foundCount', { count: history.length })}</span>
              </div>

              <div className="grid gap-3">
                {history.map((inc) => (
                  <div
                    key={inc.id}
                    onClick={() => {
                        onClose();
                        navigate(`/incidents/${inc.id}`);
                    }}
                    className="bg-surface-card border border-line rounded-sm p-4 hover:shadow-md hover:border-primary/50 transition-all cursor-pointer group relative overflow-hidden"
                  >
                    {/* Status Ribbon */}
                    <div className={`absolute top-0 right-0 h-full w-1 ${inc.status === 'NEW' ? 'bg-accent' : 'bg-primary'}`} />

                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-primary text-[11px] uppercase tracking-tighter bg-primary-soft px-2 py-0.5 rounded">{inc.ticket_id}</span>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded text-ink-inverse ${inc.priority === 1 ? 'bg-status-danger' : 'bg-status-neutral'}`}>
                           P{inc.priority}
                        </span>
                      </div>
                      <span className="text-[9px] font-mono text-ink-secondary font-bold flex items-center gap-1 bg-surface-panel px-2 py-0.5 rounded">
                        <Calendar size={10}/> {new Date(inc.report_date).toLocaleDateString()}
                      </span>
                    </div>

                    <h4 className="font-black text-ink text-[12px] mb-1 group-hover:text-primary transition-colors uppercase">{inc.description}</h4>
                    <p className="text-ink-secondary text-[10px] line-clamp-2 italic mb-3 font-medium normal-case">
                        {inc.long_description || t('incidentHistoryModal.noDetailedLog')}
                    </p>

                    <div className="flex items-center justify-between border-t pt-3 mt-1 border-line-subtle">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 text-[9px] text-ink-secondary font-bold uppercase">
                          <User size={12} className="text-primary/60"/> {inc.reported_by}
                        </div>
                        <div className={`text-[9px] font-black uppercase flex items-center gap-1 ${inc.status === 'NEW' ? 'text-accent' : 'text-primary'}`}>
                           <Activity size={10}/> {inc.status}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-primary font-black text-[9px] uppercase opacity-0 group-hover:opacity-100 transition-all">
                        {t('incidentHistoryModal.viewTicket')} <ExternalLink size={10} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-surface-card p-3 border-t flex justify-end">
           <button onClick={onClose} className="px-6 py-1.5 bg-primary-dark text-ink-inverse font-black rounded-sm text-[10px] uppercase hover:bg-primary transition-all">{t('common.close', 'Close')}</button>
        </div>
      </div>
    </div>
  );
};

export default IncidentHistoryModal;
